import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaPizzaSlice,
  FaUtensils,
  FaGlassCheers,
  FaLemon,
  FaBirthdayCake,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";

// Map icon names from DB to actual react-icons
const iconMapping = {
  FaPizzaSlice: <FaPizzaSlice />,
  FaUtensils: <FaUtensils />,
  FaGlassCheers: <FaGlassCheers />,
  FaLemon: <FaLemon />,
  FaBirthdayCake: <FaBirthdayCake />,
};

const FoodAndBeverage = () => {
  const [foodCards, setFoodCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSectionVisible, setIsSectionVisible] = useState(true);

  useEffect(() => {
    const fetchFoodAndBeverage = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/food-and-beverages`
        );
        if (response?.data?.success) {
          setFoodCards(response.data.data || []);
          if (response.data.settings) {
            setIsSectionVisible(response.data.settings.isSectionVisible);
          }
        }
      } catch (err) {
        console.error("Error fetching food and beverage data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFoodAndBeverage();
  }, []);

  if (!isLoading && !isSectionVisible) {
    return null;
  }

  return (
    <div
      id="food-beverage"
      className="w-full py-20 px-3 sm:px-6 md:px-8 lg:px-10 bg-[#050733] select-none text-white border-t border-white/10"
    >
      <div className="max-w-[1600px] mx-auto flex flex-col items-center">
        {/* Section Heading */}
        <div className="flex flex-col items-center gap-4">
          <h2 className="text-3xl sm:text-5xl md:text-6xl font-heading font-black text-white uppercase text-center tracking-tight leading-tight">
            BUILD YOUR FOOD &amp; BEVERAGE EXPERIENCE
          </h2>
        </div>

        {/* Subtitle */}
        <p className="mt-4 text-gray-300 font-subheading text-base sm:text-lg max-w-3xl text-center leading-relaxed">
          Customizable food and refreshment options to refuel your players.
          Pre-order freshly prepared artisan pizzas, finger-food platters, and
          hydration stations for your party.
        </p>

        <>
          {/* Cards displayed in ONE row on desktop */}
            {isLoading ? (
              <div className="mt-12 text-center text-primaryBtnHoverColor animate-pulse font-heading font-bold">
                LOADING MENU...
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3.5 sm:gap-4 w-full mt-12 items-stretch">
                {foodCards.map((card) => (
                  <div
                    key={card._id || card.idStr}
                    className="bg-[#090d36] border border-white/10 rounded-2xl p-4 sm:p-5 flex flex-col justify-between shadow-md hover:border-white/25 transition-all duration-200"
                  >
                    <div>
                      {/* Header */}
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#060927] border border-white/10 flex items-center justify-center text-primaryBtnHoverColor text-lg flex-shrink-0 shadow-inner">
                          {iconMapping[card.iconName] || <FaUtensils />}
                        </div>
                        <div>
                          <h3 className="text-xs sm:text-sm font-heading font-bold text-white uppercase tracking-wide leading-tight">
                            {card.title}
                          </h3>
                          <p className="text-[10px] sm:text-[11px] font-subheading font-semibold text-primaryBtnHoverColor mt-0.5 leading-tight">
                            {card.tagline}
                          </p>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-gray-300 font-subheading text-[11px] sm:text-xs leading-relaxed mt-3 min-h-[46px]">
                        {card.desc}
                      </p>

                      {/* Popular Selections */}
                      {card.options && card.options.length > 0 && (
                        <div className="mt-4">
                          <span className="text-[10px] font-heading font-extrabold text-gray-400 uppercase tracking-wider block mb-2">
                            POPULAR SELECTIONS:
                          </span>

                          <div className="space-y-2">
                            {card.options.map((option, idx) => (
                              <div
                                key={idx}
                                className="p-2.5 rounded-xl border border-white/10 bg-[#060927] flex items-start gap-2.5"
                              >
                                <div className="w-1.5 h-1.5 rounded-full bg-primaryBtnHoverColor mt-1.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <h4 className="text-white font-subheading text-xs font-semibold leading-snug">
                                    {option.name}
                                  </h4>
                                  <p className="text-gray-400 font-subheading text-[10px] leading-tight mt-0.5">
                                    {option.desc}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Card Footer note */}
                    <div className="mt-4 pt-3 border-t border-white/10 flex items-center gap-1.5 text-[10px] text-gray-400 font-subheading">
                      <HiSparkles className="text-primaryBtnHoverColor text-xs flex-shrink-0" />
                      <span>
                        Paper plates, napkins &amp; cutlery included with food
                        orders
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bottom Menu Info Bar */}
            <div className="w-full max-w-4xl mt-12 bg-[#090d36] border border-white/15 rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left shadow-lg">
              <div>
                <div className="flex items-center justify-center sm:justify-start gap-2 text-primaryBtnHoverColor text-xs font-heading font-bold uppercase tracking-wider">
                  <HiSparkles />
                  <span>HASSLE-FREE CELEBRATION DINING</span>
                </div>
                <h4 className="text-white font-heading font-bold text-base sm:text-lg mt-1">
                  Food &amp; beverage selections can be added anytime during
                  your party request or confirmed with our coordinator.
                </h4>
                <p className="text-gray-400 font-subheading text-xs mt-0.5">
                  Outside birthday cakes, cupcakes, and party favors are always
                  welcome at no extra charge!
                </p>
              </div>
            </div>
          </>
      </div>
    </div>
  );
};

export default FoodAndBeverage;
